import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  delay = 200,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate position based on trigger element
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        break;
      case 'top':
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
    }

    setCoords({ top, left });
  }, [placement]);

  // Update position when visible
  useEffect(() => {
    if (!isVisible) return;
    updatePosition();
  }, [isVisible, updatePosition]);

  // Dismiss on click outside (works for both mobile and desktop)
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        tooltipRef.current && !tooltipRef.current.contains(target)
      ) {
        setIsVisible(false);
      }
    };

    // Small delay to avoid immediate dismissal from the same click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (isTouchDevice) return;
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (isTouchDevice) return;
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  const handleTouchStart = useCallback(() => {
    setIsTouchDevice(true);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!isTouchDevice) return;
    e.stopPropagation();
    setIsVisible(prev => !prev);
  }, [isTouchDevice]);

  const getTooltipStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
    };

    switch (placement) {
      case 'bottom':
        return { ...base, top: coords.top, left: coords.left, transform: 'translateX(-50%)' };
      case 'top':
        return { ...base, top: coords.top, left: coords.left, transform: 'translate(-50%, -100%)' };
      case 'left':
        return { ...base, top: coords.top, left: coords.left, transform: 'translate(-100%, -50%)' };
      case 'right':
        return { ...base, top: coords.top, left: coords.left, transform: 'translateY(-50%)' };
      default:
        return { ...base, top: coords.top, left: coords.left, transform: 'translate(-50%, -100%)' };
    }
  };

  const getArrowStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 8,
      height: 8,
      background: '#111827', // bg-gray-900
      transform: 'rotate(45deg)',
    };

    switch (placement) {
      case 'bottom':
        return { ...base, top: -4, left: '50%', marginLeft: -4 };
      case 'top':
        return { ...base, bottom: -4, left: '50%', marginLeft: -4 };
      case 'left':
        return { ...base, right: -4, top: '50%', marginTop: -4 };
      case 'right':
        return { ...base, left: -4, top: '50%', marginTop: -4 };
      default:
        return { ...base, bottom: -4, left: '50%', marginLeft: -4 };
    }
  };

  const tooltipContent = (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          style={getTooltipStyle()}
          className="px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg"
        >
          {content}
          <div style={getArrowStyle()} />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
      >
        {children}
      </div>
      {createPortal(tooltipContent, document.body)}
    </>
  );
};
